import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import RestUtilities from '../../services/RestUtilities';
import { Booking } from '../../@types/api';
import DeleteIcon from '@mui/icons-material/Delete';
import { roundToCurrencyAsString } from '../../Functions';
import { regionTypes } from '../../Constants';
import { toastState } from '../../state/RecoilState';
import { useSetRecoilState } from 'recoil';

const vatTypes = {
  NOVAT: 1,
  CURRENTVAT: 3,
  OLDVAT: 2,
};
const vatRates = {
  1: 0,
  2: 0.175,
  3: 0.2,
};

const feeTypes = {
  BOOKINGFEE: 1,
  ARTISTFEE: 2,
  SHOWCASEDEPOSIT: 3,
};

interface Props {
  id: string | undefined;
  booking: Booking | undefined;
  setBookingSafely: (value: any) => void;
  open: boolean;
  setOpen: (value: boolean) => void;
  feeTypeId: 1 | 2 | 3;
  setFeeTypeId: (value: 1 | 2 | 3) => void;
  feeAmount: string;
  setFeeAmount: (value: string) => void;
  rateId: 1 | 2 | 3;
  setRateId: (value: 1 | 2 | 3) => void;
  location: string;
}

const tableContainer = {
  marginBottom: 1,
  marginTop: 1,
};

const formControlModal = {
  width: '48%',
  my: 1,
  mr: '2%',
};

const FeesDialogue: React.FC<Props> = ({
  id,
  booking,
  feeTypeId,
  feeAmount,
  rateId,
  open,
  location,
  setRateId,
  setFeeTypeId,
  setFeeAmount,
  setBookingSafely,
  setOpen,
}) => {
  const setToast = useSetRecoilState(toastState);
  const [feeAmountWithVat, setFeeAmountWithVat] = useState<string>('250');
  const [suggestedPrice, setSuggestedPrice] = useState<string>('Not found');

  useEffect(() => {
    const found = regionTypes.find((r) => r.name === location);
    if (found && booking?.artist?.artistPrice) {
      const price = booking.artist.artistPrice[found.id];
      setSuggestedPrice(String(price) ?? 'Not found');
    }
  }, [booking?.artist?.artistPrice, location]);

  const handleAddFee = async () => {
    let res;
    const newFee = {
      bookingId: id,
      feeTypeId,
      feeAmount,
      rateId,
    };
    res = await RestUtilities.post('/api/booking/addfee', newFee);
    if (res.ok) {
      setBookingSafely(res.content);
      setOpen(false);
      setToast('Fee added');
    }
  };

  const deleteFee = async (fee: any) => {
    const res = await RestUtilities.delete(
      `/api/booking/deletefee/${fee.bookingFeeId}`
    );
    if (res.ok) {
      setBookingSafely(res.content);
      setOpen(false);
      setToast('Fee deleted');
    }
  };

  return (
    <>
      <TableContainer component={Paper} sx={tableContainer}>
        <Table aria-label='fee table' size='small'>
          <TableHead>
            <TableRow>
              <TableCell>FeeType</TableCell>
              <TableCell align='right'>Amount</TableCell>
              <TableCell align='right'>VAT rate</TableCell>
              <TableCell align='right'>Total</TableCell>
              <TableCell align='right'>Paid</TableCell>
              <TableCell align='right'></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {booking?.bookingFees?.map((item: any) => (
              <TableRow key={item.bookingFeeId}>
                <TableCell>{item.feeType.feeTypeDescription}</TableCell>
                <TableCell align='right'>{item.feeAmount}</TableCell>
                <TableCell align='right'>
                  {item.rate.rateValue * 100}%
                </TableCell>
                <TableCell align='right'>
                  {Math.round(item.feeAmount * (1 + item.rate.rateValue))}
                </TableCell>
                <TableCell align='right'>
                  {item.feePayment?.paymentAmount}
                </TableCell>
                <TableCell align='right' onClick={(e) => deleteFee(item)}>
                  <IconButton aria-label='delete'>
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Add Fee</DialogTitle>
        <DialogContent>
          <FormControl sx={formControlModal}>
            <InputLabel id='feeType-label'>Fee Type</InputLabel>
            <Select
              labelId='feeType'
              value={feeTypeId}
              label='Fee Type'
              size='small'
              onChange={(event) => {
                const selectedFeeType = Number(event.target.value);
                const rateToUse =
                  selectedFeeType === feeTypes.ARTISTFEE
                    ? vatTypes.NOVAT
                    : vatTypes.CURRENTVAT;
                setRateId(rateToUse as 1 | 2 | 3);
                setFeeTypeId(selectedFeeType as 1 | 2 | 3);
              }}
            >
              <MenuItem value={feeTypes.SHOWCASEDEPOSIT}>
                Showcase Deposit
              </MenuItem>
              <MenuItem value={feeTypes.BOOKINGFEE}>Booking Fee</MenuItem>
              <MenuItem value={feeTypes.ARTISTFEE}>Artist Fee</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={formControlModal}>
            <TextField
              required
              id='feeAmount'
              label='Fee Amount'
              value={feeAmount}
              size='small'
              onChange={(event) => {
                const value = event.target.value;
                setFeeAmount(value);
                setFeeAmountWithVat(
                  roundToCurrencyAsString(
                    Number(value) * (1 + vatRates[rateId])
                  )
                );
              }}
            />
          </FormControl>
          <FormControl sx={formControlModal}>
            <InputLabel id='vatRate-label'>Vat Rate</InputLabel>
            <Select
              labelId='vatRate-label'
              label='Vat Rate'
              size='small'
              value={rateId}
              onChange={(event) => {
                const value = Number(event.target.value) as 1 | 2 | 3;
                setFeeAmountWithVat(
                  roundToCurrencyAsString(
                    Number(feeAmount) * (1 + vatRates[value])
                  )
                );
                setRateId(event.target.value as 1 | 2 | 3);
              }}
            >
              <MenuItem value={vatTypes.NOVAT}>No Vat</MenuItem>
              <MenuItem value={vatTypes.CURRENTVAT}>20%</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={formControlModal}>
            <TextField
              required
              id='feeAmount'
              label='FeeAmountIncVat'
              value={feeAmountWithVat}
              size='small'
              onChange={(event) => {
                const value = event.target.value;
                setFeeAmount(
                  String(parseFloat(value) / (1 + vatRates[rateId]))
                );
                setFeeAmountWithVat(roundToCurrencyAsString(value));
              }}
            />
          </FormControl>
          {feeTypeId === feeTypes.ARTISTFEE && (
            <div>SuggestedPrice: {suggestedPrice}</div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color='primary'>
            Cancel
          </Button>
          <Button onClick={handleAddFee} color='primary'>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default FeesDialogue;
